/**
 * Seam boundary for the people/teams/roles directory — WIRED to the API
 * (ENG-180 W5 + management, A10). One `permissionSubjects` query returns the org's
 * users (with their team + role memberships), teams (with members), and roles,
 * mapped to `PeopleData`; create/assign/add-member mutations manage them and
 * refetch the directory.
 */

import { notifications } from "@mantine/notifications";
import { useCallback, useMemo } from "react";
import {
	useAddTeamMemberMutation,
	useAssignRoleMutation,
	useCreateRoleMutation,
	useCreateTeamMutation,
	usePermissionSubjectsQuery,
	useRemoveTeamMemberMutation,
} from "../../graphql/generated/types.gen";
import { useActiveOrganization } from "../../hooks/useActiveOrganization";
import { EMPTY_PEOPLE, gqlToPeopleData } from "./mappers";
import type { PeopleSeam } from "./types";

const REFETCH = ["PermissionSubjects"];

const onMutationError = (action: string) => (error: unknown) =>
	notifications.show({
		title: `Couldn't ${action}`,
		message: error instanceof Error ? error.message : "Please try again.",
		color: "red",
	});

export function usePeopleAndTeams(): PeopleSeam {
	const { activeOrganizationId } = useActiveOrganization();
	const organizationId = activeOrganizationId ?? "";
	const skip = organizationId === "";

	const { data, loading } = usePermissionSubjectsQuery({
		variables: { organizationId },
		skip,
	});
	const [createTeamMutation] = useCreateTeamMutation();
	const [addMemberMutation] = useAddTeamMemberMutation();
	const [removeMemberMutation] = useRemoveTeamMemberMutation();
	const [createRoleMutation] = useCreateRoleMutation();
	const [assignRoleMutation] = useAssignRoleMutation();

	const people = useMemo(
		() => (data ? gqlToPeopleData(data.permissionSubjects) : EMPTY_PEOPLE),
		[data],
	);

	const createTeam = useCallback(
		(name: string, color?: string) => {
			if (skip) return;
			void createTeamMutation({
				variables: { organizationId, name, color },
				refetchQueries: REFETCH,
				onError: onMutationError("create the team"),
			});
		},
		[skip, organizationId, createTeamMutation],
	);

	const addTeamMember = useCallback(
		(teamId: string, userId: string) => {
			if (skip) return;
			void addMemberMutation({
				variables: { organizationId, teamId, userId },
				refetchQueries: REFETCH,
				onError: onMutationError("add the member"),
			});
		},
		[skip, organizationId, addMemberMutation],
	);

	const removeTeamMember = useCallback(
		(teamId: string, userId: string) => {
			if (skip) return;
			void removeMemberMutation({
				variables: { organizationId, teamId, userId },
				refetchQueries: REFETCH,
				onError: onMutationError("remove the member"),
			});
		},
		[skip, organizationId, removeMemberMutation],
	);

	const createRole = useCallback(
		(name: string, description?: string, color?: string) => {
			if (skip) return;
			void createRoleMutation({
				variables: { organizationId, name, description, color },
				refetchQueries: REFETCH,
				onError: onMutationError("create the role"),
			});
		},
		[skip, organizationId, createRoleMutation],
	);

	const assignRole = useCallback(
		(userId: string, roleId: string | null) => {
			if (skip) return;
			void assignRoleMutation({
				variables: { organizationId, userId, roleId },
				refetchQueries: REFETCH,
				onError: onMutationError("assign the role"),
			});
		},
		[skip, organizationId, assignRoleMutation],
	);

	return {
		people,
		loading,
		createTeam,
		addTeamMember,
		removeTeamMember,
		createRole,
		assignRole,
	};
}
